// 캐릭터 선택·생성 화면(docs/CHARACTER-PRD.md §3): 슬롯 목록(받는 중·삭제 대기·이 기기에만 표시) · 선택 · 이름 정하기/바꾸기 · 삭제(이름 입력 확인)·복구 ·
// 생성(직업 카드 5장 — 준비 중 직업은 잠금, 외형 색 3, 이름#태그 실시간 검사). 서버 오류는 characterModel.failureText 한 줄로.
// 두 곳에서 연다: 부팅(지금 캐릭터가 없음 — 게임을 만들기 전, main.ts) · 게임 안(타이틀·마을 HUD의 「캐릭터」).
// 게임 루프와 무관하다(DOM 이벤트로만 그린다). #ui 안에 붙어 세로 폰 회전(viewport.ts)을 그대로 따른다.
import { FONT } from './dom';
import { setWindowOpenFlag } from './rpgWindowEvent';
import { assetUrl } from '../pwa/assetMap';
import {
  SLOT_BASE, SLOT_MAX, characterLevel, checkCharacterName, createCharacter, currentCharacter, deleteCharacter, listCharacters,
  refreshCharacters, renameCharacter, restoreCharacter, selectCharacter, type CharacterFailure, type ClassId, type LocalCharacter,
} from '../characters/index';
import type { KV } from '../account/keys';
import {
  LOOK_NAMES, classCards, classInfo, confirmMatches, failureText, lookFilter, nameHint, slotSummary, type CharacterRow, type FailureContext,
} from './characterModel';

export interface CharacterScreenHost {
  /** 게임 안에서 연 화면인가(닫기 단추 · 지금 캐릭터 삭제 금지). 부팅 화면은 고를 때까지 닫을 수 없다 */
  inGame: boolean;
  /** 고른 캐릭터로 시작: 호스트가 게임 저장을 쓰고 새로고침한다(지금 캐릭터는 selectCharacter로 이미 정해졌다) */
  play(id: string): void;
  /** 게임 안 화면 닫기 */
  closed?(): void;
  /** 화면 위 안내 한 줄(이관 직후 「이름을 정해 주세요」 등) */
  notice?: string;
  /**
   * 첫 실행 준비(이 기기에 진행이 없고 계정 이관 판정 전): 목록 대신 「준비 중」을 보이고 계정 클라이언트가 판정(이관 없음·다른 기기 캐릭터 받기)을 끝내
   * 새로고침하기를 기다린다. 서버에 닿지 않으면 잠시 뒤 「서버 없이 시작」(원래 키로 플레이 — 다음 접속에 캐릭터로 옮겨진다)
   */
  preparing?: { offline(): void; waitMs?: number };
}

const STYLE_ID = 'dh-cs-style';
const GOLD = '#F2C14E';
const CSS =
  '.dh-cs{position:absolute;inset:0;z-index:2000;display:none;pointer-events:auto;container-type:size;container-name:dhcs;box-sizing:border-box;' +
  'padding:max(10px,var(--dh-st,0px)) max(12px,var(--dh-sr,0px)) max(10px,var(--dh-sb,0px)) max(12px,var(--dh-sl,0px));' +
  'background:radial-gradient(ellipse at 50% 30%,rgba(44,62,108,.92),rgba(9,12,24,.97) 70%);color:#eef2ff;font:600 14px/1.35 ' + FONT + ';touch-action:pan-y;user-select:none}' +
  '.dh-cs[data-open="1"]{display:flex;justify-content:center}' +
  '.dh-cs *{box-sizing:border-box}' +
  '.dh-cs-panel{width:min(1000px,100%);height:100%;display:flex;flex-direction:column;gap:8px;min-height:0}' +
  '.dh-cs-head{display:flex;align-items:center;gap:10px;flex:0 0 auto}' +
  '.dh-cs-h1{font:900 22px ' + FONT + ';color:#ffe07a;letter-spacing:.5px;margin:0}' +
  '.dh-cs-slots{font:700 12px ' + FONT + ';color:#b9c2de}' +
  '.dh-cs-sp{flex:1 1 auto}' +
  '.dh-cs-btn{min-height:40px;min-width:40px;padding:6px 14px;border-radius:10px;border:1.5px solid rgba(242,193,78,.6);background:linear-gradient(180deg,#26335a,#151d38);' +
  'color:' + GOLD + ';font:800 14px ' + FONT + ';cursor:pointer;white-space:nowrap}' +
  '.dh-cs-btn[data-primary="1"]{background:linear-gradient(#ffd877,#e0a52f);color:#2a1d05;border-color:#fff1b8}' +
  '.dh-cs-btn[data-danger="1"]{color:#ffb3b8;border-color:rgba(255,90,100,.7)}' +
  '.dh-cs-btn:disabled{opacity:.42;cursor:default}' +
  '.dh-cs-msg{flex:0 0 auto;min-height:18px;font:700 13px ' + FONT + ';color:#cfd8f5}' +
  '.dh-cs-msg[data-tone="err"]{color:#ffb3b8}' +
  '.dh-cs-msg[data-tone="ok"]{color:#a8f0c0}' +
  '.dh-cs-notice{flex:0 0 auto;padding:8px 12px;border-radius:10px;border:1px solid rgba(242,193,78,.5);background:rgba(242,193,78,.12);color:#ffe9a8;font:700 13px ' + FONT + '}' +
  '.dh-cs-body{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;padding:2px}' +
  // 슬롯 목록
  '.dh-cs-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,260px),1fr));gap:10px}' +
  '.dh-cs-card{display:flex;gap:10px;padding:10px;border-radius:14px;border:1.5px solid rgba(242,193,78,.35);background:linear-gradient(180deg,rgba(36,48,90,.95),rgba(18,25,48,.95));min-width:0}' +
  '.dh-cs-card[data-current="1"]{border-color:#fff1b8;box-shadow:0 0 16px rgba(242,193,78,.4)}' +
  '.dh-cs-card[data-state="deleting"],.dh-cs-card[data-state="download"],.dh-cs-card[data-state="locked"]{border-style:dashed}' +
  '.dh-cs-face{flex:0 0 auto;width:64px;height:80px;border-radius:10px;overflow:hidden;background:#0d1326;position:relative;border:1px solid rgba(255,255,255,.18)}' +
  '.dh-cs-face img{width:100%;height:100%;object-fit:cover;object-position:50% 12%;display:block}' +
  '.dh-cs-face[data-dim="1"] img{filter:grayscale(.85) brightness(.75)!important}' +
  '.dh-cs-fallback{position:absolute;inset:0;display:none;align-items:center;justify-content:center;font:900 26px ' + FONT + ';color:#0d1326}' +
  '.dh-cs-info{display:flex;flex-direction:column;gap:3px;min-width:0;flex:1 1 auto}' +
  '.dh-cs-name{font:900 16px ' + FONT + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
  '.dh-cs-name[data-unnamed="1"]{color:#b9c2de;font-style:italic}' +
  '.dh-cs-sub{font:700 12px ' + FONT + ';color:#cfd6ee}' +
  '.dh-cs-state{font:700 12px ' + FONT + ';color:#ffd76a}' +
  '.dh-cs-acts{display:flex;flex-wrap:wrap;gap:6px;margin-top:auto;padding-top:4px}' +
  '.dh-cs-acts .dh-cs-btn{min-height:36px;padding:4px 10px;font-size:13px}' +
  '.dh-cs-tag{font:800 11px ' + FONT + ';color:#2a1d05;background:#ffd76a;border-radius:6px;padding:1px 6px;margin-left:6px;vertical-align:2px}' +
  '.dh-cs-empty{display:flex;align-items:center;justify-content:center;gap:8px;min-height:104px;border-radius:14px;border:2px dashed rgba(242,193,78,.45);' +
  'background:rgba(20,28,54,.6);color:' + GOLD + ';font:900 16px ' + FONT + ';cursor:pointer}' +
  // 생성
  '.dh-cs-create{display:flex;flex-direction:column;gap:10px}' +
  '.dh-cs-classes{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px}' +
  '.dh-cs-class{position:relative;display:flex;flex-direction:column;align-items:stretch;gap:4px;padding:6px;border-radius:12px;border:1.5px solid rgba(242,193,78,.3);' +
  'background:linear-gradient(180deg,#24305a,#141b36);color:#fff;cursor:pointer;font:inherit;text-align:center;min-width:0}' +
  '.dh-cs-class[aria-pressed="true"]{border-color:#fff1b8;box-shadow:0 0 14px rgba(242,193,78,.5)}' +
  '.dh-cs-class[data-locked="1"] .dh-cs-cface img{filter:grayscale(1) brightness(.55)!important}' +
  '.dh-cs-cface{position:relative;width:100%;aspect-ratio:4/5;max-height:clamp(70px,32cqh,220px);border-radius:8px;overflow:hidden;background:#0d1326}' +
  '.dh-cs-cface img{width:100%;height:100%;object-fit:cover;object-position:50% 10%;display:block}' +
  '.dh-cs-lock{position:absolute;left:0;right:0;bottom:6px;margin:auto;width:max-content;padding:2px 8px;border-radius:6px;background:rgba(10,12,24,.85);color:#ffd76a;font:800 11px ' + FONT + '}' +
  '.dh-cs-cl{font:900 14px ' + FONT + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
  '.dh-cs-cr{font:600 11px ' + FONT + ';color:#b9c2de;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
  '.dh-cs-form{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px;align-items:start}' +
  '.dh-cs-detail{display:flex;flex-direction:column;gap:4px;padding:8px 10px;border-radius:12px;background:rgba(14,20,40,.75);border:1px solid rgba(255,255,255,.1)}' +
  '.dh-cs-detail b{font:900 16px ' + FONT + ';color:#ffe07a}' +
  '.dh-cs-looks{display:flex;gap:8px;flex-wrap:wrap}' +
  '.dh-cs-look{min-height:36px;padding:4px 10px;border-radius:18px;border:1.5px solid rgba(255,255,255,.35);background:#1a2240;color:#fff;font:700 12px ' + FONT + ';cursor:pointer;display:flex;align-items:center;gap:6px}' +
  '.dh-cs-look[aria-pressed="true"]{border-color:#fff1b8;background:#2c3a6a}' +
  '.dh-cs-dot{width:14px;height:14px;border-radius:50%;border:1.5px solid rgba(255,255,255,.7)}' +
  '.dh-cs-namebox{display:flex;flex-direction:column;gap:6px}' +
  '.dh-cs-input{width:100%;min-height:42px;padding:8px 12px;border-radius:10px;border:1.5px solid rgba(242,193,78,.6);background:#0e1430;color:#fff;font:800 17px ' + FONT + ';user-select:text;-webkit-user-select:text}' +
  '.dh-cs-input:focus{outline:2px solid #fff1b8;outline-offset:1px}' +
  '.dh-cs-hint{font:700 12px ' + FONT + ';color:#b9c2de;min-height:16px}' +
  '.dh-cs-hint[data-ok="1"]{color:#a8f0c0}.dh-cs-hint[data-ok="0"]{color:#ffb3b8}' +
  '.dh-cs-row{display:flex;gap:8px;flex-wrap:wrap}' +
  // 확인 창(이름·삭제)
  '.dh-cs-modal{position:absolute;inset:0;display:none;align-items:center;justify-content:center;background:rgba(5,8,18,.6);z-index:2}' +
  '.dh-cs-modal[data-open="1"]{display:flex}' +
  '.dh-cs-dlg{width:min(420px,calc(100% - 24px));max-height:calc(100% - 16px);overflow-y:auto;display:flex;flex-direction:column;gap:8px;padding:14px;border-radius:14px;border:2px solid ' + GOLD + ';' +
  'background:linear-gradient(180deg,rgba(26,34,62,.98),rgba(12,17,34,.98));box-shadow:0 8px 24px rgba(0,0,0,.6)}' +
  '.dh-cs-dlg h2{margin:0;font:900 18px ' + FONT + ';color:#ffe07a}' +
  '.dh-cs-dlg p{margin:0;font:600 13px/1.45 ' + FONT + ';color:#dfe5f8}' +
  // 낮은 가로 화면(폰 가로·세로 폰을 돌린 화면): 간격·글자를 줄이고 생성 화면은 직업 카드를 왼쪽 2단으로
  '@container dhcs (max-height:430px){' +
  '.dh-cs-h1{font-size:18px}.dh-cs-btn{min-height:36px;padding:4px 10px;font-size:13px}.dh-cs-panel{gap:5px}' +
  '.dh-cs-card{padding:7px;gap:8px}.dh-cs-face{width:52px;height:64px}.dh-cs-empty{min-height:84px}' +
  '.dh-cs-create{flex-direction:row;align-items:flex-start}.dh-cs-classes{flex:0 0 52%;grid-template-columns:repeat(5,minmax(0,1fr));gap:5px}' +
  '.dh-cs-class{padding:4px}.dh-cs-cr{display:none}.dh-cs-cface{max-height:none}' +
  '.dh-cs-form{flex:1 1 auto;grid-template-columns:minmax(0,1fr);gap:6px}.dh-cs-detail{padding:6px 8px}.dh-cs-input{min-height:38px;font-size:16px}}' +
  '@container dhcs (max-width:620px){.dh-cs-form{grid-template-columns:minmax(0,1fr)}}';

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent = CSS;
  document.head.appendChild(st);
}
function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  if (parent) parent.appendChild(e);
  return e;
}
function btn(parent: HTMLElement, label: string, testId: string, onClick: () => void, kind: '' | 'primary' | 'danger' = ''): HTMLButtonElement {
  const b = h('button', 'dh-cs-btn', parent, label);
  b.type = 'button';
  b.dataset.testid = testId;
  b.dataset.sfx = 'event';
  if (kind === 'primary') b.dataset.primary = '1';
  if (kind === 'danger') b.dataset.danger = '1';
  b.addEventListener('click', onClick);
  return b;
}
/** 직업 초상(받지 못하면 직업 색 바탕 + 첫 글자) */
function face(parent: HTMLElement, cls: ClassId, look: number, boxClass: string): HTMLDivElement {
  const info = classInfo(cls);
  const box = h('div', boxClass, parent);
  const im = h('img', '', box);
  im.alt = ''; im.draggable = false; im.decoding = 'async';
  im.style.filter = lookFilter(look);
  const fb = h('div', 'dh-cs-fallback', box, info.face.slice(0, 1));
  fb.style.background = info.color;
  im.addEventListener('error', () => { im.style.display = 'none'; fb.style.display = 'flex'; });
  im.src = assetUrl(info.portrait);
  return box;
}
const isText = (t: EventTarget | null): boolean => {
  const e = t as HTMLElement | null;
  return !!e && typeof e.tagName === 'string' && (e.tagName === 'INPUT' || e.tagName === 'TEXTAREA' || e.isContentEditable === true);
};

type View = 'list' | 'create';

export class CharacterScreen {
  private readonly root: HTMLDivElement;
  private readonly title: HTMLHeadingElement;
  private readonly slotsText: HTMLSpanElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly noticeEl: HTMLDivElement;
  private readonly msg: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly modal: HTMLDivElement;
  private readonly dlg: HTMLDivElement;
  private view: View = 'list';
  private cap = SLOT_BASE;
  private max = SLOT_MAX;
  private busy = false;
  private openFlag = false;
  private autoCreated = false;
  private refreshTry = 0;
  private refreshTimer = 0;
  private sel: { cls: ClassId; look: number; name: string } = { cls: 'kael', look: 0, name: '' };
  private readonly keyGuard = (e: KeyboardEvent): void => {
    if (!this.openFlag) return;
    if (e.code === 'Escape' || e.key === 'Escape') {
      e.stopImmediatePropagation(); e.preventDefault();
      if (this.modal.dataset.open === '1') this.closeModal();
      else if (this.view === 'create' && (this.host.inGame || this.rows().length > 0)) this.show('list');
      else if (this.host.inGame) this.close();
      return;
    }
    // 화면이 떠 있는 동안 게임 단축키(이동·스킬·퀘스트 창 등)가 뒤에서 돌지 않게 — 입력 칸 글자는 그대로
    if (!isText(e.target)) e.stopImmediatePropagation();
  };

  constructor(parent: HTMLElement, private readonly host: CharacterScreenHost, private readonly kv?: KV | null) {
    ensureStyle();
    this.root = h('div', 'dh-cs', parent);
    this.root.dataset.testid = 'character-screen';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', '캐릭터 선택');
    const panel = h('div', 'dh-cs-panel', this.root);
    const head = h('div', 'dh-cs-head', panel);
    this.title = h('h1', 'dh-cs-h1', head, '캐릭터 선택');
    this.slotsText = h('span', 'dh-cs-slots', head);
    this.slotsText.dataset.testid = 'cs-slots';
    h('span', 'dh-cs-sp', head);
    this.closeBtn = btn(head, '✕', 'cs-close', () => this.close());
    this.closeBtn.setAttribute('aria-label', '닫기');
    this.closeBtn.hidden = !host.inGame;
    this.noticeEl = h('div', 'dh-cs-notice', panel);
    this.noticeEl.hidden = true;
    this.msg = h('div', 'dh-cs-msg', panel);
    this.msg.dataset.testid = 'cs-msg';
    this.msg.setAttribute('role', 'status');
    this.body = h('div', 'dh-cs-body', panel);
    this.modal = h('div', 'dh-cs-modal', this.root);
    this.dlg = h('div', 'dh-cs-dlg', this.modal);
    this.dlg.setAttribute('role', 'alertdialog');
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal && !this.busy) this.closeModal(); });
  }

  get isOpen(): boolean { return this.openFlag; }
  /** 계정 클라이언트가 새로고침해도 되는가(입력 중·요청 중·확인 창이 아니면) */
  idle(): boolean { return !this.busy && this.modal.dataset.open !== '1' && !(this.view === 'create' && this.sel.name.length > 0); }

  /** 첫 실행 준비 화면(목록 없이 안내만 — 계정 클라이언트가 판정 뒤 새로고침한다) */
  private renderPreparing(): void {
    const p = this.host.preparing!;
    this.title.textContent = '캐릭터 준비';
    this.slotsText.textContent = '';
    this.body.textContent = '';
    const box = h('div', 'dh-cs-detail', this.body);
    box.dataset.testid = 'cs-preparing';
    h('b', '', box, '서버와 연결해 캐릭터를 준비하는 중…');
    h('div', 'dh-cs-sub', box, '이 계정의 캐릭터를 확인하고 있어요. 처음이면 곧 캐릭터를 만들 수 있어요.');
    const row = h('div', 'dh-cs-row', box);
    row.hidden = true;
    h('div', 'dh-cs-sub', row, '서버에 닿지 않으면 서버 없이 먼저 시작할 수 있어요(그 진행은 다음 접속에 캐릭터로 옮겨져요).');
    btn(row, '다시 시도', 'cs-prep-retry', () => location.reload());
    btn(row, '서버 없이 시작', 'cs-prep-offline', () => { this.closeForce(); p.offline(); }, 'primary');
    this.refreshTimer = window.setTimeout(() => { row.hidden = false; }, p.waitMs ?? 20_000);
  }
  /** 부팅 화면을 닫는다(첫 실행 「서버 없이 시작」 — 게임을 원래 키로 만든다) */
  private closeForce(): void {
    this.openFlag = false;
    setWindowOpenFlag(this.root, false);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = 0; }
    window.removeEventListener('keydown', this.keyGuard, { capture: true });
  }

  open(view: View = 'list', notice?: string): void {
    if (!this.openFlag) {
      this.openFlag = true;
      setWindowOpenFlag(this.root, true);
      // 열 때 눌려 있던 게임 입력을 놓게 한다(입력 수집기는 창 blur에서 모든 키를 놓는다)
      try { window.dispatchEvent(new Event('blur')); } catch { /* 무시 */ }
      window.addEventListener('keydown', this.keyGuard, { capture: true });
    }
    const n = notice ?? this.host.notice;
    this.noticeEl.hidden = !n;
    this.noticeEl.textContent = n ?? '';
    this.refreshTry = 0;
    if (this.host.preparing) { this.renderPreparing(); return; }
    this.show(view);
    void this.refresh();
  }

  close(): void {
    if (!this.openFlag || !this.host.inGame) return;
    this.openFlag = false;
    setWindowOpenFlag(this.root, false);
    this.closeModal();
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = 0; }
    window.removeEventListener('keydown', this.keyGuard, { capture: true });
    this.host.closed?.();
  }

  private say(text: string, tone: '' | 'err' | 'ok' = ''): void {
    this.msg.textContent = text;
    if (tone) this.msg.dataset.tone = tone; else delete this.msg.dataset.tone;
  }
  private fail(f: CharacterFailure, ctx: FailureContext): void { this.say(failureText(f, ctx), 'err'); }

  private rows(): LocalCharacter[] { return listCharacters(this.kv === undefined ? undefined : this.kv); }
  private currentId(): string | null { return currentCharacter(this.kv === undefined ? undefined : this.kv)?.id ?? null; }
  private store(): KV | null | undefined { return this.kv === undefined ? undefined : this.kv; }

  /** 서버 목록과 맞추기(받는 중 캐릭터는 이때 받는다). 세션 준비 전(no-session)이면 잠시 뒤 몇 번 다시 */
  private async refresh(): Promise<void> {
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = 0; }
    if (this.view === 'list') this.say('서버와 목록을 맞추는 중…');
    const r = await refreshCharacters(this.store());
    if (!this.openFlag) return;
    if (r.ok) {
      this.cap = r.value.slots.cap; this.max = r.value.slots.max;
      if (this.view === 'list') this.say('');
    } else if ((r.failure.code === 'no-session' || r.failure.code === 'offline') && this.refreshTry < 6) {
      this.refreshTry++;
      if (this.view === 'list') this.say(r.failure.code === 'no-session' ? '서버 연결을 준비하는 중…' : failureText(r.failure, 'list'), r.failure.code === 'offline' ? 'err' : '');
      this.refreshTimer = window.setTimeout(() => { void this.refresh(); }, 1500 * this.refreshTry);
    } else if (this.view === 'list') this.fail(r.failure, 'list');
    if (this.view === 'list') this.renderList();
    // 부팅 화면에 캐릭터가 하나도 없으면 바로 생성으로(PRD §3.1)
    if (!this.host.inGame && !this.autoCreated && this.view === 'list' && r.ok && this.rows().length === 0) { this.autoCreated = true; this.show('create'); }
  }

  private show(view: View): void {
    this.view = view;
    this.title.textContent = view === 'list' ? '캐릭터 선택' : '새 캐릭터';
    this.root.setAttribute('aria-label', this.title.textContent);
    this.say('');
    if (view === 'list') this.renderList(); else this.renderCreate();
  }

  // ── 목록
  private renderList(): void {
    const s = slotSummary(this.rows(), this.cap, this.max, { currentId: this.currentId(), inGame: this.host.inGame, now: Date.now(), levelOf: (c) => characterLevel(c, this.store()) });
    this.slotsText.textContent = s.text;
    this.body.textContent = '';
    const grid = h('div', 'dh-cs-grid', this.body);
    grid.dataset.testid = 'cs-list';
    for (const r of s.rows) this.card(grid, r);
    for (let i = 0; i < s.empty; i++) {
      const e = h('button', 'dh-cs-empty', grid, '＋ 새 캐릭터');
      e.type = 'button'; e.dataset.testid = 'cs-new'; e.dataset.sfx = 'event';
      e.addEventListener('click', () => this.show('create'));
    }
    if (s.empty === 0) {
      const full = h('div', 'dh-cs-empty', grid, '칸이 모두 찼어요');
      full.style.cursor = 'default'; full.style.color = '#b9c2de';
      full.dataset.testid = 'cs-full';
    }
  }

  private card(parent: HTMLElement, r: CharacterRow): void {
    const c = h('div', 'dh-cs-card', parent);
    c.dataset.testid = 'cs-card';
    c.dataset.id = r.id;
    c.dataset.state = r.state;
    if (r.current) c.dataset.current = '1';
    const f = face(c, r.cls, r.look, 'dh-cs-face');
    if (r.state !== 'ready' && r.state !== 'localOnly') f.dataset.dim = '1';
    const info = h('div', 'dh-cs-info', c);
    const nm = h('div', 'dh-cs-name', info, r.title);
    nm.dataset.testid = 'cs-name';
    if (r.unnamed) nm.dataset.unnamed = '1';
    if (r.current) h('span', 'dh-cs-tag', nm, '플레이 중');
    h('div', 'dh-cs-sub', info, r.sub);
    if (r.stateText) { const st = h('div', 'dh-cs-state', info, r.stateText); st.dataset.testid = 'cs-state'; }
    const acts = h('div', 'dh-cs-acts', info);
    if (r.canRestore) btn(acts, '복구', 'cs-restore', () => void this.restore(r), 'primary');
    else {
      const play = btn(acts, r.current && this.host.inGame ? '계속하기' : '시작', 'cs-play', () => this.play(r), 'primary');
      play.disabled = !r.canPlay;
    }
    if (!r.canRestore) {
      const rn = btn(acts, r.rename === 'name' ? '이름 정하기' : '이름', 'cs-rename', () => this.openRename(r));
      if (r.rename === 'locked') { rn.disabled = true; rn.title = '무료 이름 변경을 이미 썼어요'; }
      if (r.state === 'download') rn.disabled = true;
      if (r.canDelete) btn(acts, '삭제', 'cs-delete', () => this.openDelete(r), 'danger');
    }
  }

  private play(r: CharacterRow): void {
    if (this.busy) return;
    if (r.current && this.host.inGame) { this.close(); return; }
    if (!selectCharacter(r.id, this.store())) { this.say(r.state === 'download' ? '아직 받는 중이에요. 잠시 뒤 다시 눌러 주세요' : '이 캐릭터는 지금 고를 수 없어요', 'err'); this.renderList(); return; }
    this.busy = true;
    this.say('「' + r.title + '」(으)로 시작하는 중…', 'ok');
    this.host.play(r.id);
  }

  private async restore(r: CharacterRow): Promise<void> {
    if (this.busy) return;
    this.busy = true; this.say('복구하는 중…');
    const res = await restoreCharacter(r.id, this.store());
    this.busy = false;
    if (res.ok) { this.say('「' + r.title + '」을(를) 복구했어요', 'ok'); this.renderList(); }
    else { this.fail(res.failure, 'restore'); this.renderList(); }
  }

  // ── 확인 창
  private closeModal(): void { this.modal.dataset.open = '0'; this.dlg.textContent = ''; }
  private openModal(title: string): HTMLDivElement {
    this.dlg.textContent = '';
    h('h2', '', this.dlg, title);
    this.modal.dataset.open = '1';
    return this.dlg;
  }

  private openRename(r: CharacterRow): void {
    const d = this.openModal(r.rename === 'name' ? '이름 정하기' : '이름 바꾸기');
    this.dlg.dataset.testid = 'cs-rename-dlg';
    h('p', '', d, r.rename === 'name'
      ? '채팅·친구·레이드에 보일 이름이에요. 2~8자 한글·영문·숫자, 서버가 #태그 4자리를 붙여요.'
      : '무료로 한 번 바꿀 수 있어요(그 뒤 변경은 준비 중). 바꾸면 #태그도 새로 받아요.');
    const input = h('input', 'dh-cs-input', d);
    input.type = 'text'; input.maxLength = 16; input.autocomplete = 'off'; input.spellcheck = false;
    input.dataset.testid = 'cs-rename-input';
    input.setAttribute('aria-label', '새 이름');
    input.value = r.unnamed ? '' : r.title.replace(/#\d{4}$/, '');
    const hint = h('div', 'dh-cs-hint', d);
    hint.dataset.testid = 'cs-rename-hint';
    const row = h('div', 'dh-cs-row', d);
    const ok = btn(row, r.rename === 'name' ? '정하기' : '바꾸기', 'cs-rename-ok', () => void submit(), 'primary');
    btn(row, '취소', 'cs-rename-cancel', () => { if (!this.busy) this.closeModal(); });
    const update = (): void => {
      const n = nameHint(checkCharacterName(input.value));
      hint.textContent = n.text; hint.dataset.ok = n.ok ? '1' : '0';
      ok.disabled = !n.ok || this.busy;
    };
    const submit = async (): Promise<void> => {
      if (this.busy || ok.disabled) return;
      this.busy = true; ok.disabled = true; hint.textContent = '서버에 예약하는 중…'; delete hint.dataset.ok;
      const res = await renameCharacter(r.id, input.value, this.store());
      this.busy = false;
      if (res.ok) {
        this.closeModal();
        this.say('이름을 「' + (res.value.name ?? '') + '#' + String(res.value.tag ?? 0).padStart(4, '0') + '」(으)로 정했어요', 'ok');
        this.renderList();
      } else { hint.textContent = failureText(res.failure, 'rename'); hint.dataset.ok = '0'; ok.disabled = false; }
    };
    input.addEventListener('input', update);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
    update();
    input.focus();
  }

  private openDelete(r: CharacterRow): void {
    const d = this.openModal('캐릭터 삭제');
    this.dlg.dataset.testid = 'cs-delete-dlg';
    h('p', '', d, '「' + r.title + '」(' + r.sub + ')을(를) 삭제합니다. ' + (r.graceDelete
      ? 'Lv 30 이상이라 7일 동안 복구할 수 있어요(그동안 이름 예약 유지).'
      : '바로 영구 삭제돼요. 되돌릴 수 없어요.'));
    h('p', '', d, '확인하려면 ' + (r.unnamed ? '「delete」' : '이름 「' + r.confirmWord + '」') + '을(를) 입력해 주세요.');
    const input = h('input', 'dh-cs-input', d);
    input.type = 'text'; input.maxLength = 16; input.autocomplete = 'off'; input.spellcheck = false;
    input.dataset.testid = 'cs-delete-input';
    input.setAttribute('aria-label', '삭제 확인 입력');
    const hint = h('div', 'dh-cs-hint', d);
    const row = h('div', 'dh-cs-row', d);
    const ok = btn(row, '삭제', 'cs-delete-ok', () => void submit(), 'danger');
    btn(row, '취소', 'cs-delete-cancel', () => { if (!this.busy) this.closeModal(); });
    const update = (): void => { ok.disabled = this.busy || !confirmMatches(input.value, r.confirmWord); };
    const submit = async (): Promise<void> => {
      if (this.busy || ok.disabled) return;
      this.busy = true; ok.disabled = true; hint.textContent = '삭제하는 중…';
      const res = await deleteCharacter(r.id, r.confirmWord, this.store());
      this.busy = false;
      if (res.ok) {
        this.closeModal();
        this.say(res.value.purgeAt ? '「' + r.title + '」을(를) 삭제 대기로 옮겼어요(7일 안에 복구 가능)' : '「' + r.title + '」을(를) 삭제했어요', 'ok');
        this.renderList();
      } else { hint.textContent = failureText(res.failure, 'delete'); hint.dataset.ok = '0'; update(); }
    };
    input.addEventListener('input', update);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
    update();
    input.focus();
  }

  // ── 생성
  private renderCreate(): void {
    this.slotsText.textContent = '';
    this.body.textContent = '';
    const wrap = h('div', 'dh-cs-create', this.body);
    wrap.dataset.testid = 'cs-create';
    const grid = h('div', 'dh-cs-classes', wrap);
    const form = h('div', 'dh-cs-form', wrap);
    const detail = h('div', 'dh-cs-detail', form);
    detail.dataset.testid = 'cs-class-detail';
    const looks = h('div', 'dh-cs-looks', detail);
    const nameBox = h('div', 'dh-cs-namebox', form);
    const input = h('input', 'dh-cs-input', nameBox);
    input.type = 'text'; input.maxLength = 16; input.autocomplete = 'off'; input.spellcheck = false; input.placeholder = '이름(2~8자)';
    input.dataset.testid = 'cs-name-input';
    input.setAttribute('aria-label', '캐릭터 이름');
    input.value = this.sel.name;
    const hint = h('div', 'dh-cs-hint', nameBox);
    hint.dataset.testid = 'cs-name-hint';
    const row = h('div', 'dh-cs-row', nameBox);
    const make = btn(row, '만들기', 'cs-create-ok', () => void submit(), 'primary');
    const back = btn(row, '돌아가기', 'cs-create-back', () => { if (!this.busy) this.show('list'); });
    const cards: { cls: ClassId; b: HTMLButtonElement; face: HTMLDivElement }[] = [];

    const paintDetail = (): void => {
      const info = classInfo(this.sel.cls);
      detail.textContent = '';
      const t = h('div', '', detail);
      h('b', '', t, info.label + ' · ' + info.face);
      h('div', 'dh-cs-sub', detail, info.role + ' · ' + info.weapon + ' · 난이도 ' + '★'.repeat(info.stars) + '☆'.repeat(3 - info.stars));
      if (!info.playable) h('div', 'dh-cs-state', detail, '준비 중인 직업이에요(모델·전투 작업 중) — 지금은 검사·궁수를 만들 수 있어요');
      detail.appendChild(looks);
      looks.textContent = '';
      for (let i = 0; i < LOOK_NAMES.length; i++) {
        const lb = h('button', 'dh-cs-look', looks);
        lb.type = 'button'; lb.dataset.testid = 'cs-look-' + i; lb.dataset.sfx = 'event';
        lb.setAttribute('aria-pressed', i === this.sel.look ? 'true' : 'false');
        const dot = h('span', 'dh-cs-dot', lb);
        dot.style.background = info.color; dot.style.filter = lookFilter(i);
        h('span', '', lb, LOOK_NAMES[i]);
        lb.addEventListener('click', () => { this.sel.look = i; paintDetail(); for (const c of cards) (c.face.firstChild as HTMLImageElement).style.filter = lookFilter(c.cls === this.sel.cls ? i : 0); });
      }
    };
    const paintCards = (): void => {
      for (const c of cards) {
        c.b.setAttribute('aria-pressed', c.cls === this.sel.cls ? 'true' : 'false');
        (c.face.firstChild as HTMLImageElement).style.filter = lookFilter(c.cls === this.sel.cls ? this.sel.look : 0);
      }
    };
    const update = (): void => {
      this.sel.name = input.value;
      const n = nameHint(checkCharacterName(input.value));
      hint.textContent = input.value.length === 0 ? '이름은 겹쳐도 돼요 — 서버가 #태그로 구분해요' : n.text;
      if (input.value.length === 0) delete hint.dataset.ok; else hint.dataset.ok = n.ok ? '1' : '0';
      make.disabled = this.busy || !n.ok || !classInfo(this.sel.cls).playable;
    };
    for (const info of classCards()) {
      const b = h('button', 'dh-cs-class', grid);
      b.type = 'button'; b.dataset.testid = 'cs-class-' + info.id; b.dataset.sfx = 'event';
      b.setAttribute('aria-label', info.label + ' ' + info.face + (info.playable ? '' : ' (준비 중)'));
      if (!info.playable) b.dataset.locked = '1'; // 골라 보면 안내만(만들기는 잠김) — 누를 수 있어야 하므로 aria-disabled는 쓰지 않는다
      const f = face(b, info.id, 0, 'dh-cs-cface');
      if (!info.playable) h('span', 'dh-cs-lock', f, '준비 중');
      h('span', 'dh-cs-cl', b, info.label);
      h('span', 'dh-cs-cr', b, info.face + ' · ' + info.weapon);
      b.addEventListener('click', () => { this.sel.cls = info.id; if (!info.playable) this.sel.look = 0; paintCards(); paintDetail(); update(); });
      cards.push({ cls: info.id, b, face: f });
    }
    const submit = async (): Promise<void> => {
      update();
      if (this.busy || make.disabled) return;
      this.busy = true; make.disabled = true; back.disabled = true;
      hint.textContent = '서버에 이름#태그를 예약하는 중…'; delete hint.dataset.ok;
      const res = await createCharacter({ cls: this.sel.cls, look: this.sel.look, name: input.value }, this.store());
      this.busy = false; back.disabled = false;
      if (!res.ok) { hint.textContent = failureText(res.failure, 'create'); hint.dataset.ok = '0'; make.disabled = false; return; }
      const made = res.value;
      this.sel = { cls: 'kael', look: 0, name: '' };
      this.show('list');
      this.say('「' + (made.name ?? '') + '#' + String(made.tag ?? 0).padStart(4, '0') + '」을(를) 만들었어요. 「시작」을 누르면 그 캐릭터로 시작해요', 'ok');
      // 부팅 화면에서 첫 캐릭터를 만들었으면 바로 그 캐릭터로 시작
      if (!this.host.inGame && this.rows().filter((x) => !x.deletedAt).length === 1) {
        const row = slotSummary(this.rows(), this.cap, this.max, { currentId: null, inGame: false, now: Date.now(), levelOf: () => 1 }).rows.find((x) => x.id === made.id);
        if (row?.canPlay) this.play(row);
      }
    };
    input.addEventListener('input', update);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
    back.hidden = !this.host.inGame && this.rows().length === 0;
    paintCards(); paintDetail(); update();
  }
}
